<script setup lang="ts">
import { computed } from 'vue';
import CkEditorInput from '@/components/CkeditorInput.vue';
import GaiaSaveState from '@/components/design/composite/GaiaSaveState.vue';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';

// Pure view over the store: draft, save state and the debounced save all live
// in reviewProjectStore because this pane is unmounted whenever its drawer
// closes and a pending save must survive that.
const reviewProjectStore = useReviewProjectStore();

// Notes are rich text (HTML), edited with the same CKEditor wrapper as kanban
// comments; an emptied editor yields '' which the store persists as null.
const draft = computed({
  get: () => reviewProjectStore.notesDraft,
  set: value => {
    reviewProjectStore.setNotesDraft(value);
  },
});
</script>

<template>
  <!-- Owns its padding: it mounts in a flush drawer, which hands over the
       body's height and drops naive's own padding. -->
  <n-flex vertical :size="8" class="notes-pane">
    <!-- The file's name is the drawer's subtitle, so this row carries only the
         save state. -->
    <n-flex align="center" justify="end" :wrap="false" :size="8">
      <gaia-save-state
        :state="reviewProjectStore.notesSaveState"
        @save="reviewProjectStore.flushNotes"
      />
    </n-flex>
    <div class="notes-editor" style="flex: 1; min-height: 0">
      <CkEditorInput v-model="draft" extended-mode placeholder="Add notes about this file…" />
    </div>
  </n-flex>
</template>

<style scoped>
.notes-pane {
  height: 100%;
  padding: 16px 20px 20px;
  box-sizing: border-box;
}

/* CkeditorInput forces a fixed 200px content height (inline styles from JS);
   in the pane the editor should fill the remaining height and scroll inside. */
.notes-editor :deep(.ck-editor) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.notes-editor :deep(.ck-editor__main) {
  flex: 1;
  min-height: 0;
}

.notes-editor :deep(.ck-editor__editable) {
  height: 100% !important;
  min-height: 0 !important;
  overflow-y: auto;
  box-sizing: border-box;
}
</style>
